import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip, type TooltipProps } from "recharts";

import type { AnalyticsPlatformDatum } from "@/types";

import { PlatformIcon } from "./platform-icon";

const numberFormatter = new Intl.NumberFormat();

function CustomTooltip({
  active,
  payload,
}: TooltipProps<number, string> & {
  payload: { name: string; value: number; platform: string }[];
}) {
  if (!active || !payload?.length) {
    return null;
  }

  const data = payload[0];

  return (
    <div
      style={{
        backgroundColor: "rgb(255 255 255)",
        border: "1px solid rgb(229 229 229)",
        borderRadius: "8px",
        padding: "12px",
        boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <span
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: PLATFORM_COLORS[data.platform],
            flexShrink: 0,
          }}
        />
        <span
          style={{
            color: "rgb(23 23 23)",
            fontWeight: 600,
            fontSize: "14px",
          }}
        >
          {data.name}
        </span>
      </div>
      <p
        style={{
          color: "rgb(64 64 64)",
          fontSize: "14px",
          marginTop: "4px",
          marginLeft: "16px",
        }}
      >
        {numberFormatter.format(data.value)}
      </p>
    </div>
  );
}

export const PLATFORM_COLORS: Record<string, string> = {
  facebook: "#1877F2",
  twitter: "#1DA1F2",
  linkedin: "#0A66C2",
  pinterest: "#E60023",
  whatsapp: "#25D366",
  slack: "#4A154B",
  instagram: "#E4405F",
  reddit: "#FF4500",
  discord: "#5865F2",
  telegram: "#0088CC",
  snapchat: "#FFFC00",
  tiktok: "#000000",
  wechat: "#09B83E",
  tumblr: "#35465C",
  mastodon: "#6364FF",
  signal: "#3A76F0",
  youtube: "#FF0000",
  direct: "#0EA5E9",
  other: "#94A3B8",
};

interface PlatformBreakdownChartProps {
  data: AnalyticsPlatformDatum[];
}

export function PlatformBreakdownChart({ data }: PlatformBreakdownChartProps) {
  if (!data.length) {
    return (
      <div className="text-muted-foreground flex h-[280px] items-center justify-center text-sm">
        No platform data for this range yet
      </div>
    );
  }

  const total = data.reduce((sum, item) => sum + item.count, 0);
  const chartData = data.map((item) => ({
    name: item.label,
    value: item.count,
    platform: item.platform,
  }));

  return (
    <div className="flex flex-col gap-6 xl:flex-row">
      <div className="h-[280px] flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={2}
            >
              {chartData.map((entry) => (
                <Cell key={entry.platform} fill={PLATFORM_COLORS[entry.platform] ?? PLATFORM_COLORS.other} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip payload={chartData} />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="flex flex-1 flex-col justify-center gap-3">
        {data.map((item) => {
          const percentage = total ? Math.round((item.count / total) * 100) : 0;
          const color = PLATFORM_COLORS[item.platform] ?? PLATFORM_COLORS.other;

          return (
            <li key={item.platform} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="size-3 rounded-full" style={{ backgroundColor: color }} aria-hidden="true" />
                <span className="flex items-center gap-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">
                  <PlatformIcon platform={item.platform} className="size-4" />
                  {item.label}
                </span>
              </div>
              <div className="text-muted-foreground flex items-center gap-3 text-sm">
                <span>{numberFormatter.format(item.count)}</span>
                <span>{percentage}%</span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
